import React, { useState, useEffect, useLayoutEffect } from 'react';

import Typography from '@mui/material/Typography';

import { graphql, useStaticQuery } from 'gatsby';
import { GatsbyImage, getImage } from 'gatsby-plugin-image';

import styled from 'styled-components';

import { fonts } from '../gatsby-theme-material-ui-top-layout/theme';

import Button from './Button';
import LinkWrapper from './LinkWrapper';
import ScrollDownArrow from './ScrollDownArrow';

import PropyCircuit from '../img/propy-usa-circuit.svg';

import {
  PROPY_SPACE_GREY_LIGHT,
  PROPY_WHITE,
  ONBOARDING_ROOT,
  BASE_URL,
} from '../utils';

import useWindowSize from '../hooks/useWindowSize';

import { BackgroundBeams } from './aceternity/BackgroundBeams';

import WaveAnimation from './WaveAnimation';

const ZoneContainer = styled.div`
  // background-color: #000000;
  width: 100%;
  overflow: hidden;
  display: flex;
  justify-content: center;
  position: relative;
  ${props => props.mobile ? `
    top: 0px;
    height: calc(${props.windowInnerHeight}px - 32px);
    align-items: end;
    min-height: 600px;
  ` : `
    top: 0px;
    height: calc(100vh - 132px);
    min-height: 600px;
    align-items: start;
  `};
`

const BackgroundBeamsContainer = styled.div`
  background-color: #000000;
  position: absolute;
  transform: rotateX(-180deg);
  ${props => props.mobile ? `
    top: 0px;
    height: calc(${props.windowInnerHeight}px - 32px);
    min-height: 600px;
  ` : `
    top: 0px;
    height: calc(100vh - 32px);
    min-height: 700px;
  `};
`

const ScrollDownArrowContainer = styled.div`
  position: absolute;
  opacity: 0.7;
  padding: ${props => props.theme.spacing(2)};
   ${props => props.mobile ? `
    top: 0px;
    height: calc(${props.windowInnerHeight}px - 32px);
    min-height: 600px;
  ` : `
    top: 0px;
    height: calc(100vh - 32px);
    min-height: 600px;
  `};
`

const Zone = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  ${props => props.mobile ? `
    align-items: end;
    padding-left: ${props.theme.spacing(2)};
    padding-right: ${props.theme.spacing(2)};
    padding-top: ${props.theme.spacing(14)};
    padding-bottom: ${props.theme.spacing(18)};
  ` : `
    align-items: center;
    margin-top: -${props.theme.spacing(4)};
    height: 100%;
  `};
`

const TypographyContainer = styled.div`
  ${props => props.mobile ? `
    max-width: 350px;
    display: flex;
    flex-direction: column;
    align-items: center;
  ` : `
    max-width: 550px;
  `};
`

const TypographySpacer = styled.div`
  margin-top: ${props => props.theme.spacing(1.5)};
  z-index: 2;
`

const OffsetGraphicContainer = styled.div`
  max-width: 550px;
  right: 400px;
  position: relative;
  width: 100%;
  .gatsby-image-wrapper {
    width: 150%;
    height: 100%;
  }
  svg {
    width: 150%;
    height: 100%;
  }
`

const MobileGraphicContainer = styled.div`
  position: absolute;
  width: 100%;
  overflow: hidden;
  height: 100%;
  .gatsby-image-wrapper {
    max-height: 545px;
    height: 100%;
    opacity: 0.4;
  }
  svg {
    max-height: 545px;
    height: 100%;
    opacity: 0.4;
  }
`

const ButtonContainer = styled.div`
  margin-top: ${props => props.theme.spacing(4)};
  ${props => props.mobile && `
    display: flex;
    flex-direction: column;
    max-width: 280px;
    width: 100%;
  `};
`

const HomePageHero = ({ 
  isConsideredMobile,
  signedIn,
}) => {

  const [isSvgReady, setIsSvgReady] = useState(false);
  const [heroWindowHeight, setHeroWindowHeight] = useState(0);

  const windowSize = useWindowSize();

  useLayoutEffect(() => {
    if(windowSize.clientHeight > 0) {
      if(isConsideredMobile) {
        setHeroWindowHeight(currentHeroWindowHeight => currentHeroWindowHeight ? currentHeroWindowHeight : windowSize.clientHeight);
      } else {
        setHeroWindowHeight(currentHeroWindowHeight => (currentHeroWindowHeight && windowSize.clientHeight < 700) ? currentHeroWindowHeight : windowSize.clientHeight);
      }
    }
  }, [windowSize, isConsideredMobile])

  useEffect(() => {
    // Using dynamic import to load the SVG
    const loadSvg = async () => {
      try {
        await import('../img/propy-usa-circuit.svg');
        setIsSvgReady(true);
      } catch (error) {
        console.error('Failed to load SVG:', error);
      }
    };
    
    loadSvg();
  }, []);

  const data = useStaticQuery(graphql`
    query {
      file(relativePath: { eq: "propy-usa-circuit.png" }) {
        childImageSharp {
          gatsbyImageData(
            layout: FULL_WIDTH
            placeholder: BLURRED
            quality: 100
          )
        }
      }
    }
  `);

  const circuitImage = getImage(data.file);

  const renderDesktopGraphic = () => {
    if (isSvgReady) {
      return (
        <OffsetGraphicContainer>
          <PropyCircuit />
        </OffsetGraphicContainer>
      );
    }
    return (
      <OffsetGraphicContainer>
        <GatsbyImage
          image={circuitImage}
          alt="Propy USA Circuit Graphic"
        />
      </OffsetGraphicContainer>
    );
  };

  const renderMobileGraphic = () => {
    if (isSvgReady) {
      return (
        <MobileGraphicContainer>
          <PropyCircuit />
        </MobileGraphicContainer>
      );
    }
    return (
      <MobileGraphicContainer>
        <GatsbyImage
          image={circuitImage}
          alt="Propy USA Circuit Graphic"
          objectFit="cover"
          objectPosition="right"
        />
      </MobileGraphicContainer>
    );
  };

  return (
    <>
    <BackgroundBeamsContainer mobile={isConsideredMobile} windowInnerHeight={heroWindowHeight ? heroWindowHeight : 900} className="w-full relative flex flex-col items-center justify-center antialiased">
      <WaveAnimation windowInnerHeight={heroWindowHeight ? heroWindowHeight : 900} />
      <div>
        <BackgroundBeams />
      </div>
      <ScrollDownArrowContainer windowInnerHeight={heroWindowHeight ? heroWindowHeight : 900} mobile={isConsideredMobile}>
        <ScrollDownArrow />
      </ScrollDownArrowContainer>
    </BackgroundBeamsContainer>
    <ZoneContainer mobile={isConsideredMobile} windowInnerHeight={heroWindowHeight ? heroWindowHeight : 900}>
      <Zone mobile={isConsideredMobile}>
        {!isConsideredMobile && renderDesktopGraphic()}
        {isConsideredMobile && renderMobileGraphic()}
        <TypographyContainer mobile={isConsideredMobile}>
          {!isConsideredMobile && <Typography sx={{ color: PROPY_SPACE_GREY_LIGHT, letterSpacing: '0.12em', fontFamily: fonts.spaceGrotesk, textTransform: 'uppercase', textShadow: '#000 1px 1px 6px' }}>CLOSE WITH CONFIDENCE</Typography>}
          <TypographySpacer>
            <Typography 
              variant="h4"
              style={{
                textShadow: '#000 1px 1px 6px',
                color: PROPY_WHITE,
                textAlign: isConsideredMobile ? 'center' : 'left',
                letterSpacing: '-0.01em',
                fontWeight: 500,
                fontSize: isConsideredMobile ? '24px' : '40px',
                lineHeight: isConsideredMobile ? '24px' : '43px',
                fontFamily: fonts.spaceGrotesk
              }}
            >
              AI assisted title and escrow onchain for real estate.
            </Typography>
          </TypographySpacer>
          <TypographySpacer>
            <Typography 
              style={{
                textShadow: '#000 1px 1px 6px',
                textAlign: isConsideredMobile ? 'center' : 'left',
                color: PROPY_WHITE,
                letterSpacing: '-0.03em',
                fontSize: isConsideredMobile ? '14px' : '16px',
                lineHeight: isConsideredMobile ? '16px' : '21px',
                fontWeight: 300,
                fontFamily: fonts.robotoMono
              }}
            >
              Propy AI reduces human work by 40%. Deeds are recorded onchain to avoid fraud. Get full transparency on your home purchase 24/7, leveraging blockchain technology and AI to revolutionize closing transactions, ensuring unparalleled transparency and security.
            </Typography>
          </TypographySpacer>
          <ButtonContainer mobile={isConsideredMobile}>
            {!signedIn &&
              <>
                <LinkWrapper link={`${ONBOARDING_ROOT}/get-started`} external={true} noFlex={true}>
                  <Button
                    text="Get Started"
                    marginRight={isConsideredMobile ? '0px' : '16px'}
                    marginBottom={isConsideredMobile ? '16px' : '0px'}
                    width={isConsideredMobile ? "100%" : "auto"}
                  />
                </LinkWrapper>
                <LinkWrapper link={`${ONBOARDING_ROOT}/get-started/agent`} external={true} noFlex={true}>
                  <Button
                    text="Book a Demo"
                    color="secondary"
                    width={isConsideredMobile ? "100%" : "auto"}
                  />
                </LinkWrapper>
              </>
            }
            {signedIn &&
              <>
                <LinkWrapper link={`${BASE_URL}/dashboard`} external={true} noFlex={true}>
                  <Button
                    text="Go To Platform"
                    marginRight={isConsideredMobile ? '0px' : '16px'}
                    marginBottom={isConsideredMobile ? '16px' : '0px'}
                    width={isConsideredMobile ? "100%" : "auto"}
                  />
                </LinkWrapper>
                <LinkWrapper link={`${ONBOARDING_ROOT}/drop-contract`} external={true} noFlex={true}>
                  <Button
                    text="Open Escrow"
                    color="secondary"
                    width={isConsideredMobile ? "100%" : "auto"}
                  />
                </LinkWrapper>
              </>
            }
          </ButtonContainer>
        </TypographyContainer>
      </Zone>
    </ZoneContainer>
    </>
  );
};

export default HomePageHero;